import React, { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  ArrowLeft, 
  Edit3, 
  CheckCircle, 
  XCircle, 
  AlertCircle, 
  Search, 
  Filter,
  RefreshCw,
  Eye,
  Package,
  User,
  FileText,
  DollarSign,
  ChevronUp,
  ChevronDown,
  ArrowUpDown
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { getBatchLineItems, updateLineItem, validateLineItem, reprocessBatch } from '../../config/api';
import LineItemEditModal from '../../components/LineItemEditModal';

const BatchLineItems = () => {
  const { batchId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [validationFilter, setValidationFilter] = useState('');
  const [selectedLineItem, setSelectedLineItem] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [sortField, setSortField] = useState('lineNo');
  const [sortDirection, setSortDirection] = useState('asc');

  const { data: lineItemsData, isLoading, error } = useQuery({
    queryKey: ['batchLineItems', batchId, statusFilter, validationFilter],
    queryFn: () => getBatchLineItems(batchId, {
      status: statusFilter || undefined,
      validationStatus: validationFilter || undefined 
    }),
    enabled: !!batchId,
    retry: 1
  });

  const updateMutation = useMutation({
    mutationFn: updateLineItem,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['batchLineItems', batchId] });
      toast.success('Line item updated successfully');
      setShowEditModal(false);
      setSelectedLineItem(null);
    },
    onError: (error) => {
      toast.error('Failed to update line item');
      console.error('Update error:', error);
    }
  });

  const validateMutation = useMutation({
    mutationFn: validateLineItem,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['batchLineItems', batchId] });
      toast.success('Line item re-validated successfully');
    },
    onError: (error) => {
      toast.error('Failed to validate line item');
      console.error('Validation error:', error);
    }
  });

  const reprocessMutation = useMutation({
    mutationFn: () => reprocessBatch(batchId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['batchLineItems', batchId] });
      queryClient.invalidateQueries({ queryKey: ['batches'] });
      toast.success('Batch re-processed successfully');
    },
    onError: (error) => {
      toast.error('Failed to re-process batch');
      console.error('Reprocess error:', error);
    }
  });

  const filteredAndSortedLineItems = useMemo(() => {
    if (!lineItemsData?.lineItems) return [];
    
    let filtered = lineItemsData.lineItems.filter(lineItem => {
      const matchesSearch = !searchTerm || 
        lineItem.claim?.claimNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
        lineItem.claim?.customer?.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        lineItem.product?.name.toLowerCase().includes(searchTerm.toLowerCase());
      
      return matchesSearch;
    });

    // Sort the filtered results
    filtered.sort((a, b) => {
      let aValue, bValue;
      
      switch (sortField) {
        case 'lineNo':
          aValue = a.lineNo || 0;
          bValue = b.lineNo || 0;
          break;
        case 'productName':
          aValue = a.product?.name || '';
          bValue = b.product?.name || '';
          break;
        case 'customerName':
          aValue = a.claim?.customer?.name || '';
          bValue = b.claim?.customer?.name || '';
          break;
        case 'contractNumber':
          aValue = a.claim?.contract?.number || '';
          bValue = b.claim?.contract?.number || '';
          break;
        case 'quantity':
          aValue = parseFloat(a.quantity) || 0;
          bValue = parseFloat(b.quantity) || 0;
          break;
        case 'salesPrice':
          aValue = parseFloat(a.salesPrice) || 0;
          bValue = parseFloat(b.salesPrice) || 0;
          break;
        case 'totalAmount':
          aValue = (parseFloat(a.quantity) || 0) * (parseFloat(a.salesPrice) || 0);
          bValue = (parseFloat(b.quantity) || 0) * (parseFloat(b.salesPrice) || 0);
          break;
        case 'validationStatus':
          aValue = a.validationStatus || '';
          bValue = b.validationStatus || '';
          break;
        case 'salesDate':
          aValue = new Date(a.salesDate || 0);
          bValue = new Date(b.salesDate || 0);
          break;
        default:
          aValue = a[sortField] || '';
          bValue = b[sortField] || '';
      }

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortDirection === 'asc' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [lineItemsData, searchTerm, sortField, sortDirection]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) {
      return <ArrowUpDown className="w-4 h-4 text-gray-400" />;
    }
    return sortDirection === 'asc' 
      ? <ChevronUp className="w-4 h-4 text-blue-600" />
      : <ChevronDown className="w-4 h-4 text-blue-600" />;
  };

  const getValidationStatusInfo = (status) => {
    const statusConfig = {
      // General statuses
      PENDING: { color: 'bg-gray-100 text-gray-800', icon: AlertCircle, label: 'Pending' },
      VALIDATED: { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Validated' },
      MANUALLY_EDITED: { color: 'bg-blue-100 text-blue-800', icon: Edit3, label: 'Manually Edited' },
      
      // Contract validation statuses
      CNTRDT: { color: 'bg-red-100 text-red-800', icon: FileText, label: 'Contract Date Mismatch' },
      CNTRNF: { color: 'bg-red-100 text-red-800', icon: FileText, label: 'Contract Not Found' },
      CNTRIN: { color: 'bg-red-100 text-red-800', icon: FileText, label: 'Contract Inactive' },
      CNTRXP: { color: 'bg-red-100 text-red-800', icon: FileText, label: 'Contract Expired' },
      CNTRNC: { color: 'bg-yellow-100 text-yellow-800', icon: FileText, label: 'Contract Not Effective' },
      CNTRCA: { color: 'bg-red-100 text-red-800', icon: FileText, label: 'Customer Not Associated' },
      CNTRNP: { color: 'bg-red-100 text-red-800', icon: FileText, label: 'Product Not in Contract' },
      
      // Product validation statuses
      PRODNF: { color: 'bg-red-100 text-red-800', icon: Package, label: 'Product Not Found' },
      PRODIN: { color: 'bg-red-100 text-red-800', icon: Package, label: 'Product Inactive' },
      PRODNDC: { color: 'bg-yellow-100 text-yellow-800', icon: Package, label: 'Invalid NDC Format' },
      PRODCS: { color: 'bg-orange-100 text-orange-800', icon: Package, label: 'Controlled Substance' },
      PRODOOS: { color: 'bg-yellow-100 text-yellow-800', icon: Package, label: 'Out of Stock' },
      
      // Customer validation statuses
      CUSTNF: { color: 'bg-red-100 text-red-800', icon: User, label: 'Customer Not Found' },
      CUSTIN: { color: 'bg-red-100 text-red-800', icon: User, label: 'Customer Inactive' },
      CUSTNC: { color: 'bg-red-100 text-red-800', icon: User, label: 'Customer Not Compliant' },
      
      // Pricing validation statuses
      PRICNF: { color: 'bg-red-100 text-red-800', icon: DollarSign, label: 'Pricing Not Found' },
      PRICVT: { color: 'bg-red-100 text-red-800', icon: DollarSign, label: 'Price Variance' },
      PRICNG: { color: 'bg-red-100 text-red-800', icon: DollarSign, label: 'Negative Pricing' },
      PRICUH: { color: 'bg-yellow-100 text-yellow-800', icon: DollarSign, label: 'Unusually High Price' },
      
      // Sales data validation statuses
      SALDNF: { color: 'bg-red-100 text-red-800', icon: FileText, label: 'Sales Data Not Found' },
      SALDPM: { color: 'bg-yellow-100 text-yellow-800', icon: FileText, label: 'Price Mismatch' },
      SALDVV: { color: 'bg-yellow-100 text-yellow-800', icon: FileText, label: 'Volume Variance' },
      
      // Business rules validation statuses
      BUSDAT: { color: 'bg-yellow-100 text-yellow-800', icon: AlertCircle, label: 'Invalid Date' },
      BUSQTY: { color: 'bg-red-100 text-red-800', icon: AlertCircle, label: 'Invalid Quantity' },
      BUSAMT: { color: 'bg-red-100 text-red-800', icon: AlertCircle, label: 'Amount Mismatch' },
      BUSCOM: { color: 'bg-red-100 text-red-800', icon: AlertCircle, label: 'Compliance Required' },
      
      // Data quality validation statuses
      DATAQTY: { color: 'bg-yellow-100 text-yellow-800', icon: AlertCircle, label: 'Data Quality' },
      DATADUP: { color: 'bg-yellow-100 text-yellow-800', icon: AlertCircle, label: 'Duplicate Data' },
      DATAMIS: { color: 'bg-red-100 text-red-800', icon: AlertCircle, label: 'Missing Data' },
      
      // Legacy statuses (for backward compatibility)
      PRODUCT_NOT_FOUND: { color: 'bg-red-100 text-red-800', icon: Package, label: 'Product Not Found' },
      CUSTOMER_NOT_FOUND: { color: 'bg-red-100 text-red-800', icon: User, label: 'Customer Not Found' },
      CONTRACT_NOT_FOUND: { color: 'bg-orange-100 text-orange-800', icon: FileText, label: 'Contract Not Found' },
      PRICING_NOT_FOUND: { color: 'bg-yellow-100 text-yellow-800', icon: DollarSign, label: 'Pricing Not Found' },
      INVALID_QUANTITY: { color: 'bg-red-100 text-red-800', icon: XCircle, label: 'Invalid Quantity' },
      INVALID_PRICE: { color: 'bg-red-100 text-red-800', icon: XCircle, label: 'Invalid Price' },
      DUPLICATE_CLAIM: { color: 'bg-purple-100 text-purple-800', icon: AlertCircle, label: 'Duplicate Claim' },
      
      // Error statuses
      ERROR: { color: 'bg-red-100 text-red-800', icon: XCircle, label: 'Error' },
      SYSTEM_ERROR: { color: 'bg-red-100 text-red-800', icon: XCircle, label: 'System Error' }
    };
    return statusConfig[status] || { color: 'bg-gray-100 text-gray-800', icon: AlertCircle, label: status };
  };

  const handleEditLineItem = (lineItem) => {
    setSelectedLineItem(lineItem);
    setShowEditModal(true);
  };

  const handleValidateLineItem = (lineItemId) => {
    validateMutation.mutate(lineItemId);
  };

  const handleSaveLineItem = (updatedLineItem) => {
    updateMutation.mutate(updatedLineItem);
  };

  const handleCloseModal = () => {
    setShowEditModal(false);
    setSelectedLineItem(null);
  };

  const handleReprocessBatch = () => {
    if (window.confirm('Are you sure you want to re-process this entire batch? This will re-validate all line items.')) {
      reprocessMutation.mutate();
    }
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => navigate('/app/batches')}
            title="Back to batches"
            className="p-2 text-gray-600 hover:text-gray-900 rounded-lg hover:bg-gray-100"
          >
            <ArrowLeft size={20} />
          </button>
          <h1 className="text-2xl font-bold text-gray-900">Batch Line Items</h1>
        </div>
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-500 mt-4">Loading line items...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => navigate('/app/batches')}
            title="Back to batches"
            className="p-2 text-gray-600 hover:text-gray-900 rounded-lg hover:bg-gray-100"
          >
            <ArrowLeft size={20} />
          </button>
          <h1 className="text-2xl font-bold text-gray-900">Batch Line Items</h1>
        </div>
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-700 mb-2">Error Loading Line Items</h2>
          <p className="text-gray-500">Failed to load line items. Please try again.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/app/batches')}
            title="Back to batches"
            className="p-2 text-gray-600 hover:text-gray-900 rounded-lg hover:bg-gray-100"
          >
            <ArrowLeft size={20} />
          </button>
          <h1 className="text-2xl font-bold text-gray-900">Batch Line Items</h1>
        </div>
        
        <div className="flex items-center gap-3">
          <button
            onClick={handleReprocessBatch}
            disabled={reprocessMutation.isPending}
            title="Re-process batch"
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            <RefreshCw size={16} className={reprocessMutation.isPending ? 'animate-spin' : ''} />
            Re-process Batch
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
            <input
              type="text"
              placeholder="Search claims..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">All Statuses</option>
            <option value="IMPORTED">Imported</option>
            <option value="VALIDATED">Validated</option>
            <option value="IN_ERROR">In Error</option>
            <option value="REJECTED">Rejected</option>
            <option value="APPROVED">Approved</option>
            <option value="POSTED">Posted</option>
          </select>
          
          <select
            value={validationFilter}
            onChange={(e) => setValidationFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">All Validation Statuses</option>
            <option value="PENDING">Pending</option>
            <option value="PRODUCT_NOT_FOUND">Product Not Found</option>
            <option value="CUSTOMER_NOT_FOUND">Customer Not Found</option>
            <option value="CONTRACT_NOT_FOUND">Contract Not Found</option>
            <option value="PRICING_NOT_FOUND">Pricing Not Found</option>
            <option value="INVALID_QUANTITY">Invalid Quantity</option>
            <option value="INVALID_PRICE">Invalid Price</option>
            <option value="DUPLICATE_CLAIM">Duplicate Claim</option>
            <option value="VALIDATED">Validated</option>
            <option value="MANUALLY_EDITED">Manually Edited</option>
          </select>
          
          <div className="text-sm text-gray-600 flex items-center">
            <Filter size={16} className="mr-2" />
            {filteredAndSortedLineItems.length} of {lineItemsData?.lineItems?.length || 0} items
          </div>
        </div>
      </div>

      {/* Sorting Controls */}
      <div className="bg-white rounded-lg shadow p-4 mb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <span className="text-sm font-medium text-gray-700">Sort by:</span>
            <select
              value={sortField}
              onChange={(e) => setSortField(e.target.value)}
              className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="lineNo">Line Number</option>
              <option value="productName">Product Name</option>
              <option value="customerName">Customer Name</option>
              <option value="contractNumber">Contract Number</option>
              <option value="quantity">Quantity</option>
              <option value="salesPrice">Unit Price</option>
              <option value="totalAmount">Total Amount</option>
              <option value="validationStatus">Validation Status</option>
              <option value="salesDate">Sales Date</option>
            </select>
            <button
              onClick={() => setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')}
              title="Toggle sort direction"
              className="flex items-center px-3 py-1 bg-gray-100 hover:bg-gray-200 rounded-md text-sm transition-colors"
            >
              {sortDirection === 'asc' ? (
                <>
                  <ChevronUp className="w-4 h-4 mr-1" />
                  Ascending
                </>
              ) : (
                <>
                  <ChevronDown className="w-4 h-4 mr-1" />
                  Descending
                </>
              )}
            </button>
          </div>
          <div className="text-sm text-gray-500">
            Currently sorted by: <span className="font-medium text-gray-700">
              {sortField === 'lineNo' && 'Line Number'}
              {sortField === 'productName' && 'Product Name'}
              {sortField === 'customerName' && 'Customer Name'}
              {sortField === 'contractNumber' && 'Contract Number'}
              {sortField === 'quantity' && 'Quantity'}
              {sortField === 'salesPrice' && 'Unit Price'}
              {sortField === 'totalAmount' && 'Total Amount'}
              {sortField === 'validationStatus' && 'Validation Status'}
              {sortField === 'salesDate' && 'Sales Date'}
            </span> ({sortDirection === 'asc' ? 'A-Z' : 'Z-A'})
          </div>
        </div>
      </div>

      {/* Line Items Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {filteredAndSortedLineItems.length === 0 ? (
          <div className="p-8 text-center">
            <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Line Items Found</h3>
            <p className="text-gray-500">
              {lineItemsData?.lineItems?.length === 0 
                ? "This batch has no line items."
                : "No line items match your current filters."
              }
            </p>
            {lineItemsData?.lineItems?.length === 0 && (
              <p className="text-sm text-gray-400 mt-2">
                Try uploading an EDI 844 file to process chargeback claims.
              </p>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('lineNo')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Line #</span>
                      {getSortIcon('lineNo')}
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('productName')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Product</span>
                      {getSortIcon('productName')}
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('quantity')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Quantity</span>
                      {getSortIcon('quantity')}
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('salesPrice')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Unit Price</span>
                      {getSortIcon('salesPrice')}
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('totalAmount')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Total Amount</span>
                      {getSortIcon('totalAmount')}
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('customerName')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Customer</span>
                      {getSortIcon('customerName')}
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('validationStatus')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Validation Status</span>
                      {getSortIcon('validationStatus')}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredAndSortedLineItems.map((lineItem) => {
                  const validationInfo = getValidationStatusInfo(lineItem.validationStatus);
                  const ValidationIcon = validationInfo.icon;
                  
                  return (
                    <tr key={lineItem.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {lineItem.lineNo}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{lineItem.product?.name || 'N/A'}</div>
                        <div className="text-sm text-gray-500">{lineItem.product?.ndc11 || ''}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {lineItem.quantity}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        ${(parseFloat(lineItem.salesPrice) || 0).toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        ${(parseFloat(lineItem.totalAmount) || 0).toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{lineItem.claim?.claimNumber || 'N/A'}</div>
                        <div className="text-sm text-gray-500">{lineItem.claim?.customer?.name || 'N/A'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${validationInfo.color}`}>
                          <ValidationIcon className="w-3 h-3 mr-1" />
                          {validationInfo.label}
                        </span>
                        {lineItem.validationErrors?.length > 0 && (
                          <div className="text-xs text-red-600 mt-1">
                            {lineItem.validationErrors.length} error(s)
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleEditLineItem(lineItem)}
                            className="text-blue-600 hover:text-blue-900 p-1"
                            title="Edit"
                          >
                            <Edit3 size={16} />
                          </button>
                          <button
                            onClick={() => handleValidateLineItem(lineItem.id)}
                            disabled={validateMutation.isPending}
                            className="text-green-600 hover:text-green-900 p-1 disabled:opacity-50"
                            title="Re-validate"
                          >
                            <CheckCircle size={16} />
                          </button>
                          <button
                            onClick={() => navigate(`/app/line-items/${lineItem.id}`)}
                            className="text-gray-600 hover:text-gray-900 p-1"
                            title="View Details"
                          >
                            <Eye size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      <LineItemEditModal
        isOpen={showEditModal}
        onClose={handleCloseModal}
        lineItem={selectedLineItem}
        onSave={handleSaveLineItem}
        isLoading={updateMutation.isPending}
      />
    </div>
  );
};

export default BatchLineItems;
